import React, { useState, useEffect } from 'react';
import { Play, ExternalLink, ArrowLeft, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const [portfolio, setPortfolio] = useState({
    categories: ['Tous'], // Start with just 'Tous', will load from backend
    projects: [],
    reels: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = selectedCategory === 'Tous' 
    ? portfolio.projects 
    : selectedCategory === 'Réels / Shorts'
    ? portfolio.reels
    : portfolio.projects.filter(project => project.category === selectedCategory);

  // Separate featured and non-featured projects (only for regular projects, not reels)
  const featuredProjects = selectedCategory === 'Réels / Shorts' 
    ? [] 
    : filteredProjects.filter(project => project.featured);
  const otherProjects = selectedCategory === 'Réels / Shorts' 
    ? filteredProjects 
    : filteredProjects.filter(project => !project.featured);

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      
      // Fetch videos from backend (public endpoint, no auth needed)
      const videosResponse = await axios.get(`${API}/portfolio-videos`);
      
      // Fetch categories from backend (public endpoint)
      let categories = ['Tous']; // Default fallback
      try {
        const categoriesResponse = await axios.get(`${API}/categories`);
        if (categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
          const backendCategories = categoriesResponse.data
            .sort((a, b) => a.display_order - b.display_order)
            .map(cat => cat.name);
          categories = ['Tous', ...backendCategories];
        }
      } catch (error) {
        console.log('Using fallback categories:', error);
        // Fallback to categories from videos if categories endpoint fails
      }
      
      if (videosResponse.data && videosResponse.data.success) {
        const videos = videosResponse.data.data;
        
        // Separate regular videos and reels/shorts
        const projects = videos
          .filter(video => video.format !== 'vertical')
          .map(video => ({
            id: video.id,
            title: video.title,
            description: video.description,
            category: video.category,
            thumbnail: video.thumbnail,
            videoUrl: `https://youtube.com/watch?v=${video.youtube_id}`,
            youtubeId: video.youtube_id,
            featured: video.featured || false,
            placeholder: video.placeholder || false,
            date: video.date,
            format: video.format
          }));

        const reels = videos
          .filter(video => video.format === 'vertical')
          .map(video => ({
            id: video.id,
            title: video.title,
            description: video.description,
            category: 'Réels / Shorts',
            thumbnail: video.thumbnail,
            videoUrl: `https://youtube.com/watch?v=${video.youtube_id}`,
            youtubeId: video.youtube_id,
            featured: false,
            placeholder: video.placeholder || false,
            date: video.date,
            format: video.format
          }));

        // If we couldn't get categories from backend, extract from videos as fallback
        if (categories.length === 1) { // Only 'Tous'
          const videoCategories = [...new Set(projects.map(video => video.category))];
          const hasReels = reels.length > 0;
          categories = ['Tous', ...videoCategories];
          if (hasReels) {
            categories.push('Réels / Shorts');
          }
        }

        setPortfolio(prev => ({
          ...prev,
          categories,
          projects,
          reels
        }));
      }
    } catch (error) {
      console.error('Failed to load portfolio data:', error);
      // Keep using default empty arrays if backend fails
    } finally {
      setLoading(false);
    }
  };

  const handleVideoPlay = (project) => {
    if (project.placeholder) return;
    
    // Track video play with Google Analytics
    if (window.trackVideoPlay) {
      window.trackVideoPlay(project.title);
    }
    
    setSelectedProject(project);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Link 
                to="/realisations"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Retour aux réalisations</span>
              </Link>
              
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 tracking-tight">
                Réalisations vidéo
              </h1>
              <div className="w-16 h-1 rounded-full mx-auto mb-8" style={{ backgroundColor: '#f97316' }}></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Découvrez mes réalisations vidéo : mariages, entreprises, prises de vues drone
                et projets sportifs.
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-16">
              {portfolio.categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'text-white shadow-lg'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                  style={selectedCategory === category ? { backgroundColor: '#f97316' } : {}}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Loading State */}
        {loading ? (
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#f97316' }}></div>
              <p className="text-gray-400">Chargement du portfolio...</p>
            </div>
          </section>
        ) : (
          <>
            {/* Featured Projects Section (Sport & Drone) */}
            {featuredProjects.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center">
                <span className="w-1 h-8 rounded-full mr-4" style={{ backgroundColor: '#f97316' }}></span>
                Projets mis en avant
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {featuredProjects.map((project) => (
                  <div 
                    key={project.id}
                    className={`group relative bg-gray-900 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl ${
                      project.placeholder ? 'opacity-60' : 'cursor-pointer'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
                      
                      {/* Play Button or Placeholder */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {project.placeholder ? (
                          <div className="flex items-center space-x-2 text-white">
                            <Clock className="h-8 w-8" />
                            <span className="text-lg font-medium">À venir</span>
                          </div>
                        ) : (
                          <button 
                            onClick={() => handleVideoPlay(project)}
                            className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-200"
                            style={{ backgroundColor: '#f97316' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
                          >
                            <Play className="h-8 w-8 text-white ml-1" />
                          </button>
                        )}
                      </div>

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="text-white px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#f97316' }}>
                          {project.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-bold text-white transition-colors">
                          {project.title}
                        </h3>
                        <span className="text-gray-400 text-sm">{project.date}</span>
                      </div>
                      
                      <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        {project.description}
                      </p>

                      {!project.placeholder && (
                        <button 
                          onClick={() => handleVideoPlay(project)}
                          className="inline-flex items-center space-x-2 text-sm font-medium group-hover:translate-x-1 transition-all"
                          style={{ color: '#f97316' }}
                          onMouseEnter={(e) => e.target.style.color = '#fb923c'}
                          onMouseLeave={(e) => e.target.style.color = '#f97316'}
                        >
                          <span>Voir la vidéo</span>
                          <Play className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Other Projects Section */}
        {otherProjects.length > 0 && selectedCategory !== 'Réels / Shorts' && (
          <section className="py-12 bg-gray-900/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl font-semibold text-gray-300 mb-8 flex items-center">
                <span className="w-1 h-6 bg-gray-500 rounded-full mr-4"></span>
                Autres Réalisations
              </h2>
              
              {/* Regular Projects - Horizontal Layout */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherProjects.map((project) => (
                  <div 
                    key={project.id}
                    className={`group relative bg-gray-800 rounded-xl overflow-hidden transition-all duration-300 shadow-md hover:shadow-lg ${
                      project.placeholder ? 'opacity-60' : 'cursor-pointer hover:transform hover:scale-105'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity"></div>
                      
                      {/* Play Button */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {!project.placeholder && (
                          <button 
                            onClick={() => handleVideoPlay(project)}
                            className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white/30 transition-all duration-200"
                          >
                            <Play className="h-6 w-6 text-white ml-0.5" />
                          </button>
                        )}
                      </div>

                      {/* Category Badge */}
                      <div className="absolute top-3 left-3">
                        <span className="bg-gray-600 text-white px-2 py-1 rounded text-xs">
                          {project.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-sm font-semibold text-white group-hover:text-gray-300 transition-colors line-clamp-2">
                          {project.title}
                        </h4>
                        <span className="text-gray-500 text-xs ml-2">{project.date}</span>
                      </div>
                      
                      <p className="text-gray-400 text-xs leading-relaxed mb-3 line-clamp-2">
                        {project.description}
                      </p>

                      {!project.placeholder && (
                        <button 
                          onClick={() => handleVideoPlay(project)}
                          className="inline-flex items-center space-x-1 text-gray-400 hover:text-gray-300 text-xs transition-colors"
                        >
                          <span>Voir</span>
                          <Play className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Reels Section - Only show for "Réels / Shorts" filter OR "Tous" filter */}
        {(selectedCategory === 'Réels / Shorts' || selectedCategory === 'Tous') && (
          <section className="py-12 bg-gray-800/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl font-semibold text-gray-300 mb-8 flex items-center">
                <span className="w-1 h-6 bg-red-600 rounded-full mr-4"></span>
                Réels / Shorts
              </h2>
              
              {/* Reels Section - Vertical Layout */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {portfolio.reels.map((reel) => (
                  <div 
                    key={reel.id}
                    className={`group relative bg-gray-800 rounded-xl overflow-hidden transition-all duration-300 shadow-md hover:shadow-lg ${
                      reel.placeholder ? 'opacity-60' : 'cursor-pointer hover:transform hover:scale-105'
                    }`}
                  >
                    {/* Vertical Thumbnail */}
                    <div className="relative aspect-[9/16] overflow-hidden">
                      <img
                        src={reel.thumbnail}
                        alt={reel.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                      
                      {/* Play Button or Placeholder */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {reel.placeholder ? (
                          <div className="text-center text-white">
                            <Clock className="h-8 w-8 mx-auto mb-2" />
                            <span className="text-sm">À venir</span>
                          </div>
                        ) : (
                          <button 
                            onClick={() => handleVideoPlay(reel)}
                            className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
                            style={{ backgroundColor: '#f97316' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
                          >
                            <Play className="h-8 w-8 text-white ml-1" />
                          </button>
                        )}
                      </div>

                      {/* Shorts Badge */}
                      <div className="absolute top-3 left-3">
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">
                          SHORTS
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h4 className="text-lg font-semibold text-white mb-2 leading-tight">
                        {reel.title}
                      </h4>
                      <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                        {reel.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Video Modal */}
        {selectedProject && (
          <VideoPlayer 
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
        </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default Portfolio;
